/**
 * @module app.shared
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { OpenFlags } from 'app/shared/flags/open-flags';
import { CompassFlags } from 'app/shared/flags/compass-flags';
import { PopupBase } from 'app/shared/elements/popup-base';
import { microTask, task } from 'app/shared/lib/decorators';
import { bindable } from 'aurelia-typed-observable-plugin';

@autoinject()
export class EssDropdownCustomElement extends PopupBase {

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected disabled = false;

    /** Whether the dropdown height is unrestricted and content is allowed to flow outside the boundaries of the viewport */
    @bindable.booleanAttr({ defaultBindingMode: bindingMode.toView })
    protected unrestrictedHeight: boolean = false;

    public direction: CompassFlags | undefined = undefined;

    // use this with show.bind="" to keep contents display:none until first open() call
    // until a direction is picked, the contents of the dropdown should not be given any space
    protected visible: boolean = false;

    // Computed "max-height" CSS value for the drop down container, if applicable
    protected maxHeight?: string;

    protected OpenFlags = OpenFlags;
    protected CompassFlags = CompassFlags;

    public openDropdown(direction: CompassFlags) {
        this.direction = direction;
        this.open();
    }

    public open() {
        // toggle dropdown overall visibility immediately
        this.visible = true;

        // recalculate (as a microTask) once the element is no longer display:none
        this.recalculate(this.direction);

        // after everything else, call delayOpen() to call super.open() to initiate default behavior
        this.delayOpen();
    }

    @task
    private delayOpen() {
        // use PopupBase implementation of open()
        super.open();
    }

    @microTask
    private recalculate(direction?: CompassFlags) {
        const { innerHeight, innerWidth } = window;

        // TODO [Matt] Check if this a sufficient direction
        if(direction === undefined) {
            const { top, height, left, width } = this.element.getBoundingClientRect();
            direction = 0;
            direction |= top + height / 2 >= innerHeight / 2 ? CompassFlags.Up : CompassFlags.Down;
            direction |= left + width / 2 >= innerWidth / 2 ? CompassFlags.Left : CompassFlags.Right;
        }
        this.direction = direction;

        // Calculate the max allowable height to keep the content contained in the current viewport
        if(this.unrestrictedHeight) {
            this.maxHeight = undefined;
        }
        else {
            // INFO[MW]: We are anchored to the parent element; opening the container above or below it.  Use that to determine the available size of the dropdown at this instance.
            // Pixels are fine as a unit because we are computing it at a single instance, based on screensize and current positions.
            // If the user changes zoom levels or positioning while the dropdown is open, this won't scale, but it should always work for an instantaneous calculation.
            const parentRect = this.element.parentElement!.getBoundingClientRect();
            if(this.direction & CompassFlags.Up) {
                const height = parentRect.top;
                this.maxHeight = `calc(${height}px - var(--ess-header-height) - (2 * var(--ess-dropdown-margin-y)))`;
            }
            else if(this.direction & CompassFlags.Down) {
                const height = innerHeight - parentRect.bottom;
                this.maxHeight = `calc(${height}px - (2 * var(--ess-dropdown-margin-y))`;
            }
        }
    }

    public toggle(direction?: CompassFlags) {
        if(this.state === OpenFlags.Closed) {
            this.direction = direction || this.direction;
            this.open();
        }
        else if(this.state === OpenFlags.Open) {
            this.close();
        }
    }

    protected handleFocusout({ relatedTarget }: FocusEvent) {
        if(!relatedTarget || !this.element.contains(relatedTarget as Element)) {
            this.close();
        }
    }

    protected async receiveFocus() {
        await Promise.resolve();
        this.element.focus();
    }

    protected asDirectionClass(direction: CompassFlags): string {
        if(!direction) { return ''; }
        return `ess-dir-${direction & CompassFlags.Down ? 'down' : 'up'}-${direction & CompassFlags.Left ? 'left' : 'right'}`;
    }
}
